class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    // Intercept item removals (capture phase) so we can play the fade +
    // collapse animation before cart.js swaps the list out. Lives on the
    // persistent host, so it survives the innerHTML re-renders below.
    this.addEventListener('click', this.onRemoveClick.bind(this), true);
    // Fade the running total in whenever it changes (add/remove).
    this.observePriceUpdates();
    // Editable per-item quantity fields (delegated — survive re-renders).
    this.addEventListener('input', this.onQtyInput.bind(this));
    this.addEventListener('keydown', this.onQtyKeydown.bind(this));
    this.addEventListener('change', this.onQtyChange.bind(this), true);
    this.addEventListener('click', this.onStepClick.bind(this));
  }

  // −/+ steppers: nudge the quantity by 1 and let cart.js apply it via change.
  onStepClick(event) {
    const step = event.target.closest('.ncart-item__step');
    if (!step || !this.contains(step)) return;
    const field = step.closest('.ncart-item__qty-group').querySelector('.ncart-item__qty');
    if (!field) return;
    const dir = parseInt(step.dataset.step, 10) || 0;
    const current = parseInt(field.value, 10) || 0;
    let next = current + dir;
    if (next < 1) next = 1; // use the × button to remove an item
    if (next > 99) next = 99; // two-digit cap
    if (next === current) return; // already at a bound — nothing to do
    field.value = String(next);
    field.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Keep the quantity field to at most two digits, numbers only.
  onQtyInput(event) {
    const field = event.target.closest('.ncart-item__qty');
    if (!field) return;
    const cleaned = field.value.replace(/\D/g, '').slice(0, 2);
    if (cleaned !== field.value) field.value = cleaned;
  }

  // Enter commits the edit (via blur) instead of submitting the form → checkout.
  onQtyKeydown(event) {
    if (event.key !== 'Enter') return;
    const field = event.target.closest('.ncart-item__qty');
    if (!field) return;
    event.preventDefault();
    field.blur();
  }

  // Empty or zero isn't a valid quantity (use the × to remove) — revert it and
  // stop the change from reaching cart.js. Runs in capture, before cart.js.
  onQtyChange(event) {
    const field = event.target.closest('.ncart-item__qty');
    if (!field) return;
    const value = field.value.replace(/\D/g, '').slice(0, 2);
    if (value === '' || value === '0') {
      field.value = field.getAttribute('value');
      event.stopImmediatePropagation();
      return;
    }
    field.value = value;
  }

  observePriceUpdates() {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    const readTotal = () => {
      const el = this.querySelector('.ncart__total');
      return el ? el.textContent.trim() : '';
    };
    this.lastTotal = readTotal();
    const observer = new MutationObserver(() => {
      const value = readTotal();
      if (value === this.lastTotal) return; // unchanged — ignore the re-render
      this.lastTotal = value;
      if (reduce.matches) return;
      const total = this.querySelector('.ncart__total');
      if (!total) return;
      total.classList.remove('is-price-flash');
      void total.offsetWidth; // restart the fade-in animation
      total.classList.add('is-price-flash');
    });
    observer.observe(this, { subtree: true, childList: true, characterData: true });
  }

  onRemoveClick(event) {
    const removeButton = event.target.closest('cart-remove-button');
    if (!removeButton || !this.contains(removeButton)) return;
    const item = removeButton.closest('.ncart-item');
    if (!item) return; // unknown markup — let cart.js handle it normally

    // Take over from cart.js's own remove handler.
    event.preventDefault();
    event.stopImmediatePropagation();

    // Ignore extra clicks while a removal is already animating.
    if (this.querySelector('.ncart-item.is-removing')) return;

    this.animateItemRemoval(item, removeButton);
  }

  animateItemRemoval(item, removeButton) {
    const cartItems = removeButton.closest('cart-drawer-items');
    const commit = () => {
      if (cartItems) {
        cartItems.updateQuantity(removeButton.dataset.index, 0, {
          currentTarget: removeButton,
          target: removeButton,
        });
      }
    };

    const startHeight = item.offsetHeight;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || !startHeight) {
      commit();
      return;
    }

    let committed = false;
    const finish = () => {
      if (committed) return;
      committed = true;
      commit(); // real removal — cart.js re-renders behind the now-gone item
    };

    const isLast = this.querySelectorAll('.ncart-item').length <= 1;

    if (isLast) {
      // Nothing below to reflow into, so don't collapse the height (that just
      // pulls the row upward) — fade it out in place. As it goes, the total
      // fades to its empty value and the checkout greys out (it stays put,
      // disabled, rather than being removed).
      item.classList.add('is-removing');
      const total = this.querySelector('.ncart__total');
      if (total) total.classList.add('is-updating');
      const checkout = this.querySelector('.ncart__checkout');
      if (checkout) checkout.classList.add('is-disabling');
      item.addEventListener('transitionend', (e) => {
        if (e.propertyName === 'opacity') finish();
      });
      setTimeout(finish, 600); // safety net if transitionend never arrives
      return;
    }

    // Phase 1 — fade out. Lock the current height so phase 2 can transition it.
    item.style.height = `${startHeight}px`;
    item.classList.add('is-removing');

    // Phase 2 — collapse to 0 height so the rows below slide up into the gap.
    // Two rAFs guarantee the locked height paints before we transition to 0,
    // so the collapse runs every time (a plain timeout sometimes skipped it).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        item.classList.add('is-collapsing');
        item.style.height = '0px';
        const total = this.querySelector('.ncart__total');
        if (total) total.classList.add('is-updating');
      });
    });

    item.addEventListener('transitionend', (e) => {
      if (e.propertyName === 'height') finish();
    });
    setTimeout(finish, 700); // safety net if transitionend never arrives
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
